import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const MODES = [
  {
    id: 'school_finalizes',
    label: 'School finalizes placements',
    blurb: 'You allocate students and confirm the placements yourself, then send each company its own final list. Companies submit internships and seats, but take no part in choosing students.',
  },
  {
    id: 'company_confirms',
    label: 'Company confirms placements',
    blurb: 'You propose students to each company and they accept, reject or ask for a replacement before you finalise.',
  },
];

/** How internship placements are approved for this school. */
export function InternshipPlacementCard() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/coordinator/school/internship-settings').then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  const choose = async (mode) => {
    if (!data || mode === data.placement_mode) return;
    setSaving(true);
    try {
      const r = await api.put('/coordinator/school/internship-settings', { placement_mode: mode });
      setData(r.data);
      toast.success('New internship programmes will use this workflow');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save this setting'); }
    setSaving(false);
  };

  return (
    <div data-testid="internship-placement-card">
      <h3 className="text-sm font-bold text-[#1F2937] mb-3 font-['Outfit']">Internships</h3>
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
        <p className="text-[11px] text-[#6B7280] leading-relaxed mb-3">
          Who decides the final placement. This applies to internship programmes you create from now on —
          programmes already taking applications keep the workflow they started with.
        </p>
        {!data ? <Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF]" /> : (
          <>
            <div className="space-y-2">
              {MODES.map(m => {
                const active = data.placement_mode === m.id;
                return (
                  <button key={m.id} onClick={() => choose(m.id)} disabled={saving}
                    className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${active ? 'border-[#F97D6A] bg-[#FFF8F6]' : 'border-gray-200 bg-white hover:border-[#9CA3AF]'}`}
                    data-testid={`placement-mode-${m.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${active ? 'border-[#F97D6A] bg-[#F97D6A]' : 'border-gray-300'}`} />
                      <span className="text-sm font-semibold text-[#1F2937]">{m.label}</span>
                      {m.id === data.default_mode && (
                        <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#6B7280]">Default</Badge>
                      )}
                      {active && <Badge className="border-0 text-[10px] bg-emerald-50 text-emerald-600">In use</Badge>}
                    </div>
                    <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed pl-5">{m.blurb}</p>
                  </button>
                );
              })}
            </div>
            {data.programs?.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1.5">
                  Current programmes
                </p>
                <ul className="space-y-1" data-testid="placement-mode-programs">
                  {data.programs.map(p => (
                    <li key={p.id} className="text-[11px] text-[#4B5563] flex items-center gap-1.5">
                      {p.locked && <Lock className="h-3 w-3 text-[#9CA3AF]" />}
                      <span className="font-medium text-[#1F2937]">{p.name}</span>
                      <span className="text-[#9CA3AF]">
                        · {MODES.find(m => m.id === p.placement_mode)?.label || p.placement_mode}
                        {p.locked ? ' (locked to this programme)' : ' (follows this setting)'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
